'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { ChevronDown, Cog, Droplets, Search, ShieldAlert } from '@/components/homepage-october/lucide'

import { Badge } from '@/components/homepage-october/ui/badge'
import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/homepage-october/ui/input-group'
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/homepage-october/ui/item'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipRows, IdChip, Section, SectionHeader, SubHeader, ViewAll, words } from '../blocks/layout'
import { useMedia } from '../hooks'
import type { Model } from '../types'
import { GarageRail } from './Specialty'

/* ------------------------------------------------------------------ helpers */

// v2's tier pill colours (proposed-v2/v2.css .v2-tier.t-*), all dark text on a light tint.
const TIER: Record<string, string> = {
  Luxury: 'bg-[#fff6db]! text-[#8a5a00]!',
  Popular: 'bg-[#eef4ff]! text-[#004aad]!',
  Value: 'bg-[#eafaf1]! text-[#087443]!',
  Budget: 'bg-[#f2f4f7]! text-[#4b5565]!',
  Niche: 'bg-[#eef5ff]! text-[#0454c0]!',
}

/** Case, accent and punctuation blind, so "cafe" finds Café and "black decker" finds Black+Decker. */
const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '')

/* ------------------------------------------------------------------ the A–Z index */

/**
 * All brands, A–Z, on a shadcn Collapsible. The content is force-mounted and only hidden while closed, so every
 * brand link is in the server HTML. Like v2 (`details[data-open-wide]`), it starts open from 768 px up.
 */
function BrandIndex({ az }: { az: Model['brands']['az'] }) {
  const wide = useMedia('(min-width: 768px)')
  const [pick, setPick] = React.useState<boolean | null>(null)
  const open = pick ?? wide
  const [q, setQ] = React.useState('')
  const [tier, setTier] = React.useState('All')
  const key = norm(q)
  const match = (it: (typeof az.items)[number]) => (!key || norm(it.name).includes(key)) && (tier === 'All' || it.tier === tier)
  const shown = az.items.filter(match).length
  return (
    <Collapsible open={open} onOpenChange={setPick} className="mt-8 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-xs">
      <h3>
        <CollapsibleTrigger className="group flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-[#f8fbff] md:px-6">
          <span aria-hidden="true" className={compactClassName("flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#eef5ff] to-[#fff1e6] text-xs font-bold tracking-tight text-[#0454c0] ring-1 ring-black/5")}>
            A–Z
          </span>
          <span className={compactClassName("flex-1 text-base font-semibold tracking-tight text-neutral-950 md:text-lg")}>{az.summary}</span>
          <ChevronDown aria-hidden="true" className="size-5 shrink-0 text-[#0469f0] transition-transform duration-200 group-data-[state=open]:rotate-180" />
        </CollapsibleTrigger>
      </h3>
      {/* Until someone toggles it, CSS decides: closed on a phone, open from 768 px. The server cannot know the
          width, so this keeps its copy the same as the live one at every width (no jump when the section goes live). */}
      <CollapsibleContent forceMount className={cn('border-t border-neutral-100 px-5 pt-4 pb-6 md:px-6', pick === null && !wide ? 'max-md:data-[state=closed]:hidden' : 'data-[state=closed]:hidden')}>
        <div className={compactClassName("flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6")}>
          <InputGroup className="h-10! w-full rounded-full! border-neutral-300! bg-white sm:max-w-xs has-[[data-slot=input-group-control]:focus-visible]:border-[#0469f0]!">
            <InputGroupAddon>
              <Search aria-hidden="true" className="size-4 text-neutral-500" />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              name="brand"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Filter brands"
              placeholder="Filter brands"
              autoComplete="off"
              spellCheck={false}
            />
          </InputGroup>
          <p role="status" className={compactClassName("text-xs font-medium text-neutral-500")}>
            {shown ? `${shown} of ${az.items.length} brands` : 'No matching brands. Try another search or filter.'}
          </p>
        </div>
        <div role="group" aria-label="Brand categories" className={compactClassName("v3-brand-filters mt-4 flex flex-wrap gap-2")}>
          {['All', ...Object.keys(TIER)].map((value) => <button key={value} type="button" aria-pressed={tier === value} onClick={() => setTier(value)}>{value}</button>)}
        </div>
        <ul className={compactClassName("v3-brand-index mt-4 columns-2 gap-x-6 sm:columns-3 lg:columns-5 xl:columns-6")}>
          {az.items.map((it, index) => (
            <li key={it.href + tier + key} hidden={!match(it)} className={compactClassName("break-inside-avoid")} style={{ animationDelay: `${(index % 8) * 25}ms` }}>
              <DestinationLink
                href={it.href}
                className="flex items-center justify-between gap-2 border-b border-dashed border-neutral-200 py-1.5 text-[13.5px] text-neutral-800 transition-colors hover:border-[#0469f0]/40 hover:text-[#0454c0]"
              >
                <span className={compactClassName("min-w-0")}>{it.name}</span>
                {it.tier ? (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'rounded-full! border-transparent! px-1.5! py-0! text-[9.5px]! font-bold! uppercase tracking-[0.06em]',
                      TIER[it.tier] ?? 'bg-[#f2f4f7]! text-[#4b5565]!',
                    )}
                  >
                    {it.tier}
                  </Badge>
                ) : null}
              </DestinationLink>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}

/* ------------------------------------------------------------------ brands (V14) */

export function Brands({ model }: { model: Model }) {
  const m = model.brands
  return (
    <Section aria={m.aria} v2="V14" tone="mist">
      <SectionHeader head={{ title: m.title, id: m.id, viewAll: m.viewAll }} tone="mist" />
      <div className={compactClassName("relative")}>
        <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0 overflow-hidden")}>
          <div className={compactClassName("absolute -top-16 left-[8%] size-72 rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.12),transparent_65%)]")} />
          <div className={compactClassName("absolute -bottom-20 right-[6%] size-80 rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.16),transparent_65%)]")} />
        </div>
        <ul className={compactClassName("relative grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6")}>
          {m.cards.map((c) => (
            <li key={c.href}>
              <DestinationLink
                href={c.href}
                aria-label={c.aria}
                className="group relative flex h-full min-h-32 flex-col items-center justify-center gap-4 overflow-hidden rounded-2xl border border-neutral-200 bg-white px-4 py-6 text-center shadow-xs transition duration-200 hover:-translate-y-0.5 hover:border-[#0469f0]/35 hover:shadow-[0_18px_40px_-24px_rgba(4,105,240,0.5)]"
              >
                <span aria-hidden="true" className={compactClassName("absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-gradient-to-r from-[#0469f0] via-[#5aa2ff] to-[#ff9951] transition-transform duration-300 group-hover:scale-x-100")} />
                <img src={c.img.src} alt={c.img.alt} loading="lazy" decoding="async" className={compactClassName("h-9 w-auto max-w-[80%] object-contain transition-transform duration-300 group-hover:scale-105")} />
                <span className={compactClassName("text-[13px] font-medium leading-snug text-neutral-700 group-hover:text-[#0454c0]")}>{c.label}</span>
              </DestinationLink>
            </li>
          ))}
        </ul>
      </div>
      <ChipRows rows={m.chiprows} />
      <div className={compactClassName("mt-10")}>
        <SubHeader title={m.moreTitle} link={m.moreLink} />
        <ul className={compactClassName("grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6")}>
          {m.logos.map((b) => (
            <li key={b.href}>
              <DestinationLink
                href={b.href}
                className="group flex h-full flex-col items-center gap-2 rounded-2xl border border-neutral-200 bg-white px-3 py-4 text-center transition duration-200 hover:-translate-y-0.5 hover:border-[#ff9951] hover:shadow-[0_14px_28px_-16px_rgba(16,24,40,0.3)]"
              >
                <span className={compactClassName("flex h-11 w-full items-center justify-center")}>
                  <img src={b.img.src} alt={b.img.alt} loading="lazy" decoding="async" className={compactClassName("max-h-10 max-w-[78%] object-contain")} />
                </span>
                <span className={compactClassName("text-[13px] font-semibold leading-tight text-neutral-900 group-hover:text-[#0454c0]")}>{b.name}</span>
                <span className={compactClassName("tabular rounded-full bg-[#f3f8ff] px-2 py-0.5 text-[11px] font-semibold text-[#0454c0]")}>{b.range}</span>
              </DestinationLink>
            </li>
          ))}
        </ul>
      </div>
      <div className={compactClassName("mt-8")}><GarageRail model={model} /></div>
      <BrandIndex az={m.az} />
    </Section>
  )
}

/* ------------------------------------------------------------------ filters and parts (V15) */

/** "LG · FFN-0000007" → ["LG", "FFN-0000007"]. */
function splitMeta(s: string): [string, string] {
  const i = s.lastIndexOf(' · ')
  return i >= 0 ? [s.slice(0, i), s.slice(i + 3)] : [s, '']
}
const isId = (s: string) => /^F[A-Z]{2}-\d+$/.test(s)

/**
 * The column's identifier family. v2 shows FFN and FPN; the model's second column has an empty `code` (the extractor
 * missed v2's other chip class), so it falls back to the family of the IDs printed on the column's own rows.
 */
function codeOf(c: Model['parts']['cols'][number]) {
  if (c.code) return c.code
  for (const r of c.rows) {
    const hit = (r.lines[1] ?? '').match(/\b(F[A-Z]{2})-\d+/)
    if (hit) return hit[1]
  }
  return ''
}

function PartsColumn({ c, k }: { c: Model['parts']['cols'][number]; k: number }) {
  const code = codeOf(c)
  const blue = k % 2 === 0
  const Icon = blue ? Droplets : Cog
  return (
    <Card className="h-full gap-0! overflow-hidden py-0! transition-shadow duration-200 hover:shadow-md">
      <div className={compactClassName(cn('flex items-center gap-3 border-b px-5 py-4', blue ? 'bg-gradient-to-r from-[#e8f2ff] via-[#f5f9ff] to-white' : 'bg-gradient-to-r from-[#fff0e3] via-[#fff8f2] to-white'))}>
        <span aria-hidden="true" className={compactClassName(cn('flex size-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-xs ring-1 ring-black/5', blue ? 'text-[#0469f0]' : 'text-[#b54708]'))}>
          <Icon className="size-5" />
        </span>
        <div className={compactClassName("flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1")}>
          {code ? (
            <Badge variant={blue ? 'outline' : 'orange'} className={cn('font-mono tracking-wide', blue && 'border-transparent! bg-[#0469f0]/10! text-[#0454c0]!')}>
              {code}
            </Badge>
          ) : null}
          <h3 className={compactClassName("text-lg font-semibold tracking-tight text-neutral-950")}>{c.title}</h3>
        </div>
      </div>
      <ul className={compactClassName("divide-y divide-neutral-100")}>
        {c.rows.map((r) => {
          const [name, meta = ''] = r.lines
          const [brand, id] = splitMeta(meta)
          return (
            <li key={r.href}>
              <Item asChild size="sm" className="flex-nowrap! gap-3.5! rounded-none! px-5! py-3.5! hover:bg-[#f8fbff]!">
                <DestinationLink href={r.href}>
                  <ItemMedia className="size-16 overflow-hidden rounded-lg border border-neutral-100 bg-white! p-1.5">
                    <Img src={r.img.src} alt={r.img.alt} className="size-full" />
                  </ItemMedia>
                  <ItemContent className="min-w-0">
                    <ItemTitle className="text-[13.5px]! leading-snug text-neutral-900">
                      <span className={compactClassName("line-clamp-2")}>{name}</span>
                    </ItemTitle>
                    <ItemDescription className="text-xs!">
                      {brand}
                      {id ? <> · {isId(id) ? <IdChip>{id}</IdChip> : id}</> : null}
                    </ItemDescription>
                  </ItemContent>
                  <span className={compactClassName("tabular shrink-0 text-base font-bold tracking-tight text-neutral-950")}>{r.price}</span>
                </DestinationLink>
              </Item>
            </li>
          )
        })}
      </ul>
      <div className={compactClassName("mt-auto border-t border-neutral-100 px-5 py-3")}>
        <ViewAll link={c.all} variant="link" className="text-[#0454c0]!" />
      </div>
    </Card>
  )
}

/** The recall check: an Alert-style band in recall red tones. v2 used an h2 here, so it keeps one. */
function RecallBand({ r }: { r: Model['parts']['recall'] }) {
  return (
    <Card id={r.id} className="relative mt-8 scroll-mt-28 gap-0! overflow-hidden border-[#fecdca]! bg-[#fef3f2]! py-0! shadow-none!">
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-[radial-gradient(circle,rgba(180,35,24,0.12),transparent_65%)]")} />
      <div className={compactClassName("relative flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between md:gap-10 md:p-8")}>
        <div className={compactClassName("flex items-start gap-4")}>
          <span aria-hidden="true" className={compactClassName("flex size-12 shrink-0 items-center justify-center rounded-2xl bg-white text-[#b42318] shadow-xs ring-1 ring-[#b42318]/15")}>
            <ShieldAlert className="size-6" />
          </span>
          <div>
            <p className={compactClassName("text-xs font-semibold uppercase tracking-[0.14em] text-[#b42318]")}>{r.eyebrow}</p>
            <h2 className={compactClassName("mt-1.5 text-balance text-2xl font-bold tracking-tight text-neutral-950 md:text-3xl")}>{r.title}</h2>
            <p className={compactClassName("mt-2 max-w-xl text-pretty text-base leading-relaxed text-neutral-700")}>{r.text}</p>
          </div>
        </div>
        <Button
          asChild
          variant="outline"
          size="lg"
          className="w-full shrink-0 rounded-full! border-[#b42318]/30! bg-white! font-semibold! text-[#b42318]! hover:bg-[#fee4e2]! hover:text-[#912018]! sm:w-auto"
        >
          <DestinationLink href={r.link.href} aria-label={r.link.aria}>
            {words(r.link.text)}
            <ArrowRight aria-hidden="true" />
          </DestinationLink>
        </Button>
      </div>
    </Card>
  )
}

export function Parts({ model }: { model: Model }) {
  const m = model.parts
  return (
    <Section aria={m.aria} v2="V15" tone="white">
      <SectionHeader head={{ title: m.title, sub: m.sub, viewAll: m.viewAll }} />
      <div className={compactClassName("grid gap-6 md:grid-cols-2")}>
        {m.cols.map((c, k) => (
          <PartsColumn key={c.title} c={c} k={k} />
        ))}
      </div>
      <RecallBand r={m.recall} />
    </Section>
  )
}
